// Site analytics: our own anonymous page views, time on site and feature use
// (shown on the admin page), plus Google Analytics for visitors who accept cookies.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GA_MEASUREMENT_ID } from './config';
import { getCurrentLang } from './langState';

const VISITOR_KEY = 'mm-vid';
const SESSION_KEY = 'mm-sid';
const NO_TRACK_KEY = 'mm-no-track';
const CONSENT_KEY = 'mm-cookie-consent';
const SESSION_GAP = 30 * 60000;
const PING_SECONDS = 15;

function store(kind) {
  try {
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function read(kind, key) {
  try {
    return store(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(kind, key, value) {
  try {
    store(kind)?.setItem(key, value);
  } catch {
    // Private mode: analytics just counts this visit less precisely.
  }
}

function newId() {
  return (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function visitorId() {
  let id = read('local', VISITOR_KEY);
  if (!id) {
    id = newId();
    write('local', VISITOR_KEY, id);
  }
  return id;
}

// The current visit: a new one after 30 minutes away.
export function sessionId() {
  const now = Date.now();
  let saved = null;
  try {
    saved = JSON.parse(read('session', SESSION_KEY) || 'null');
  } catch {
    saved = null;
  }
  const session = saved && now - saved.last < SESSION_GAP ? { ...saved, last: now } : { id: newId(), last: now, fresh: true };
  write('session', SESSION_KEY, JSON.stringify({ id: session.id, last: now }));
  return session;
}

// Page views so far in the visit, so time on site can be added to the page in front.
const VIEW_KEY = 'mm-view-n';
let currentView = null;

function nextView(sid) {
  let saved = null;
  try {
    saved = JSON.parse(read('session', VIEW_KEY) || 'null');
  } catch {
    saved = null;
  }
  const n = saved && saved.sid === sid ? saved.n + 1 : 0;
  write('session', VIEW_KEY, JSON.stringify({ sid, n }));
  currentView = { sid, n };
  return n;
}

const tracking = () => typeof window !== 'undefined' && read('local', NO_TRACK_KEY) !== '1';

function send(path, body) {
  try {
    fetch(`/api/t/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true })
      .catch(() => {});
  } catch {
    // Analytics never gets in the way of the site.
  }
}

// Admins' browsers stop counting themselves.
let forgotten = false;

// Also deletes the visits this browser already recorded, from before the admin logged in.
export function setNoTrack(on) {
  if (!on) return;
  write('local', NO_TRACK_KEY, '1');
  const vid = read('local', VISITOR_KEY);
  if (vid && !forgotten) {
    forgotten = true;
    fetch('/api/t/forget-browser', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ vid }) }).catch(() => {});
  }
}

// A feature used in the browser (for logged-in members).
export function trackEvent(name) {
  if (tracking()) send('event', { name });
  gtagEvent(name.toLowerCase().replace(/[^a-z]+/g, '_'));
}

// How far down an article the reader has got: 0, 25, 50, 75 or 100 (the end).
function readDepth(el) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  const read = (window.innerHeight - rect.top) / rect.height;
  if (read >= 0.97) return 100;
  return Math.max(0, Math.min(75, Math.floor(read * 4) * 25));
}

// Reports reading of one blog article: when it opens, each new quarter read, and
// time on it every 15 seconds while it's on screen. `ref` is the article element.
export function useArticleTracking(slug, lang, ref) {
  const deepest = useRef(0);
  useEffect(() => {
    if (!tracking() || !slug) return undefined;
    deepest.current = 0;
    const report = (seconds) => send('read', { sid: sessionId().id, vid: visitorId(), slug, lang, seconds, depth: deepest.current });
    const onScroll = () => {
      const depth = readDepth(ref.current);
      if (depth > deepest.current) {
        deepest.current = depth;
        report(0);
      }
    };
    deepest.current = readDepth(ref.current);
    report(0);
    window.addEventListener('scroll', onScroll, { passive: true });
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') report(PING_SECONDS);
    }, PING_SECONDS * 1000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearInterval(timer);
    };
  }, [slug, lang]); // eslint-disable-line react-hooks/exhaustive-deps
}

// --- Google Analytics (only after the visitor accepts cookies) ---------------

let gaLoaded = false;

export function consentChoice() {
  return read('local', CONSENT_KEY);
}

export function setConsent(choice) {
  write('local', CONSENT_KEY, choice);
  window.dispatchEvent(new CustomEvent('mm-consent'));
  if (choice === 'accepted') loadGoogleAnalytics();
}

function loadGoogleAnalytics() {
  if (gaLoaded || !GA_MEASUREMENT_ID || !tracking()) return;
  gaLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
  };
  window.gtag('js', new Date());
  // Page views are sent on every route change below.
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

function gtagEvent(name, params) {
  if (gaLoaded && window.gtag) window.gtag('event', name, params);
}

export function trackLead(source) {
  gtagEvent('generate_lead', { lead_source: source });
}

export function trackDemo() {
  gtagEvent('demo_start');
}

// A step of the visitor journey: 'demoExplore' (a demo "See:" link) or 'demoSignup'
// (a sign-up button inside the demo). Only the step name is sent.
export function trackStep(what) {
  if (tracking()) send('mark', { sid: sessionId().id, what });
  gtagEvent(what === 'demoSignup' ? 'demo_signup_click' : 'demo_explore');
}

// The free report card page: 'reportSample', 'reportUpload' (their own file) or 'reportSignup'
// (the save button). Only the step name is sent, never the file.
export function trackReport(what) {
  if (tracking()) send('mark', { sid: sessionId().id, what });
  gtagEvent({ reportSample: 'report_card_sample', reportUpload: 'report_card_upload', reportSignup: 'report_card_signup_click', reportReminder: 'report_card_reminder' }[what] || 'report_card');
}

// A tracked button (see CLICK_IDS in analytics.js on the server), e.g. 'hero-signup'.
export function trackClick(id) {
  if (tracking()) send('click', { sid: sessionId().id, id });
  gtagEvent('cta_click', { cta: id });
}

// A free calculator being used (typed into), once per visit.
const usedTools = new Set();
export function trackTool(slug) {
  if (usedTools.has(slug)) return;
  usedTools.add(slug);
  if (tracking()) send('tool', { sid: sessionId().id, slug });
  gtagEvent('tool_use', { tool: slug });
}

// A member's first trades in the journal (an import or a manual entry). No trade data is sent.
export function trackFirstTrade(method) {
  gtagEvent('first_trade', { method });
}

export function trackSignup(method = 'email') {
  gtagEvent('sign_up', { method });
}

// Sends a page view on every route change and keeps time on site while the page is in front.
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    if (consentChoice() === 'accepted') loadGoogleAnalytics();
  }, []);

  useEffect(() => {
    if (!tracking()) return;
    const session = sessionId();
    const params = new URLSearchParams(location.search);
    send('view', {
      sid: session.id,
      n: nextView(session.id),
      vid: visitorId(),
      path: location.pathname,
      referrer: session.fresh ? document.referrer : '',
      utmSource: params.get('utm_source') || params.get('ref') || '',
      utmCampaign: params.get('utm_campaign') || '',
      lang: getCurrentLang(),
      width: window.innerWidth,
    });
    gtagEvent('page_view', { page_path: location.pathname, page_location: window.location.href, page_title: document.title });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tracking()) return undefined;
    // Time on the page while it's in front, sent every 15 seconds and, for the part since the
    // last report, when the visitor leaves or switches tabs.
    let since = document.visibilityState === 'visible' ? Date.now() : null;
    const report = (beacon) => {
      if (since === null) return;
      const seconds = Math.min(60, Math.round((Date.now() - since) / 1000));
      since = document.visibilityState === 'visible' ? Date.now() : null;
      if (seconds < 1) return;
      const sid = sessionId().id;
      // A visit that ran out while the page sat open restarts here, without a page of its own.
      const body = { sid, seconds, ...(currentView && currentView.sid === sid ? { n: currentView.n } : {}) };
      if (beacon && navigator.sendBeacon) {
        try {
          navigator.sendBeacon('/api/t/ping', new Blob([JSON.stringify(body)], { type: 'application/json' }));
          return;
        } catch {
          // Falls back to a normal request.
        }
      }
      send('ping', body);
    };
    const id = setInterval(() => report(false), PING_SECONDS * 1000);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') report(true);
      else since = Date.now();
    };
    const onLeave = () => report(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onLeave);
    // A real person scrolls, taps, clicks or types; robots that load a page and leave don't.
    const events = ['scroll', 'pointerdown', 'keydown', 'touchstart', 'mousemove'];
    const onActive = () => {
      events.forEach((e) => window.removeEventListener(e, onActive));
      send('active', { sid: sessionId().id });
    };
    events.forEach((e) => window.addEventListener(e, onActive, { passive: true }));
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onLeave);
      events.forEach((e) => window.removeEventListener(e, onActive));
    };
  }, []);
}
